/* eslint-disable react/jsx-props-no-spreading */
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import InfoIcon from '@mui/icons-material/Info';
import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  TextField,
  Tooltip,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { capitalize, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Field, useForm } from 'react-final-form';
import { composeValidations, validateRequired } from './formValidations';

const icon = <CheckBoxOutlineBlankIcon fontSize='small' />;
const checkedIcon = <CheckBoxIcon fontSize='small' />;

const AutoCompleteMultipleSelect = ({
  name,
  label,
  id,
  disabled,
  required,
  options,
  freeSolo,
  loading,
  validations,
  defaultValue,
  disableClear,
  onChange,
  onInputChange,
  showCheckbox,
  formatOption,
  noOptionsText,
  renderOption: renderOptionProp,
  sortOptions,
  serverSideSearch,
  labelTooltip,
}) => {
  const form = useForm();
  const [prefilledDescriptions, setPrefilledDescriptions] = useState([]);

  const validate = composeValidations([
    ...validations,
    ...(required ? [validateRequired('Required')] : []),
  ]);

  const formatter = formatOption || capitalize;

  const handleChange = (_, value) => {
    setPrefilledDescriptions(value);
    form.change(name, value);
    if (onChange) {
      onChange(_, value);
    }
  };

  const handleInputChange = (event, value, reason) => {
    if (onInputChange) {
      onInputChange(event, value, reason);
    }
  };
  const onBlur = () => {
    form.blur(name);
  };

  useEffect(() => {
    if (!isEmpty(defaultValue)) {
      setPrefilledDescriptions(defaultValue);
    }
  }, [defaultValue]);

  return (
    <>
      {/*
       * The info icon lives INSIDE the label rather than beside it. InputLabel's `shrink` state
       * applies `translate(0,-1.5px) scale(0.75)` with a top-left origin, and a transform does
       * not change the element's layout box — so a sibling icon starts after the label's full
       * UNSCALED width, leaving a gap the width of the shrunk-away text and sitting off the
       * visual baseline. Nesting it makes the icon share the label's transform and line box, so
       * it tracks the text at any label length.
       */}
      <InputLabel
        shrink
        htmlFor={id}
        required={required}
        sx={{
          fontSize: '1.2em',
          ...(labelTooltip && {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            // Without this the label keeps the block width of its container and the tooltip
            // anchors far to the right of the text.
            width: 'fit-content',
          }),
        }}
      >
        {label}
        {labelTooltip && (
          <Tooltip title={labelTooltip} arrow placement='bottom-end'>
            {/*
             * Sized in the label's own units: the 0.75 shrink scale applies here too, so 20px
             * renders at ~15px — in line with the other info icons in the app.
             */}
            <InfoIcon sx={{ fontSize: 20, color: 'action.active' }} />
          </Tooltip>
        )}
      </InputLabel>

      <Field
        name={name}
        id={id}
        validate={validate}
        render={({ meta }) => {
          const showError = !!meta.error && !!meta.touched;
          const displayedHelperText = (!!meta.touched && meta.error) || '';
          return (
            <FormControl error={showError} variant='standard' fullWidth>
              <Autocomplete
                required={required}
                disabled={disabled}
                multiple
                freeSolo={freeSolo}
                id={id}
                disableClearable={disableClear}
                getOptionKey={(_, index) => index}
                onChange={handleChange}
                onInputChange={handleInputChange}
                onBlur={onBlur}
                // When the options already come back filtered by the API, MUI's built-in filter
                // runs a second, dumber pass over them: a plain substring match of the raw input
                // against the label. That silently drops valid results whenever the two disagree
                // on spacing or punctuation — typing "Cabergoline0.5mg" hides the "Cabergoline
                // 0.5MG" the server just returned. Same fix the other server-backed pickers use
                // (AutoSuggest, NdcAutocomplete, SigsAutoSuggest).
                {...(serverSideSearch && { filterOptions: (unfiltered) => unfiltered })}
                options={
                  sortOptions
                    ? [...new Set(options)].sort((firstValue, secondValue) =>
                        String(firstValue ?? '').localeCompare(
                          String(secondValue ?? ''),
                          undefined,
                          {
                            sensitivity: 'base',
                          }
                        )
                      )
                    : [...new Set(options)]
                }
                value={prefilledDescriptions}
                disableCloseOnSelect
                noOptionsText={noOptionsText}
                renderOption={
                  renderOptionProp ||
                  (showCheckbox
                    ? (props, option, { selected }) => {
                        // eslint-disable-next-line react/prop-types
                        const { key, ...optionProps } = props;
                        return (
                          <li key={key} {...optionProps}>
                            <Checkbox
                              icon={icon}
                              checkedIcon={checkedIcon}
                              style={{ marginRight: 8 }}
                              checked={selected}
                            />
                            {formatter(option)}
                          </li>
                        );
                      }
                    : undefined)
                }
                renderTags={(value, getTagProps) => {
                  return value.map((option, index) => {
                    // eslint-disable-next-line react/prop-types
                    const { key, ...tagProps } = getTagProps({ index });
                    return (
                      <Chip
                        variant='outlined'
                        label={
                          <Tooltip title={option}>
                            <Box
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: 100,
                              }}
                            >
                              {formatter(option)}
                            </Box>
                          </Tooltip>
                        }
                        key={key}
                        {...tagProps}
                        size='small'
                      />
                    );
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    name={name}
                    size='small'
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loading && <CircularProgress color='primary' size={20} />}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              <FormHelperText sx={{ minHeight: '0.5rem' }}>{displayedHelperText}</FormHelperText>
            </FormControl>
          );
        }}
      />
    </>
  );
};

export default AutoCompleteMultipleSelect;

AutoCompleteMultipleSelect.propTypes = {
  name: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  validations: PropTypes.arrayOf(PropTypes.func),
  required: PropTypes.bool,
  freeSolo: PropTypes.bool,
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
  defaultValue: PropTypes.arrayOf(PropTypes.string),
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  disableClear: PropTypes.bool,
  onChange: PropTypes.func,
  onInputChange: PropTypes.func,
  showCheckbox: PropTypes.bool,
  formatOption: PropTypes.func,
  noOptionsText: PropTypes.string,
  renderOption: PropTypes.func,
  sortOptions: PropTypes.bool,
  serverSideSearch: PropTypes.bool,
  labelTooltip: PropTypes.string,
};

AutoCompleteMultipleSelect.defaultProps = {
  loading: false,
  required: false,
  freeSolo: false,
  disabled: false,
  validations: [],
  defaultValue: [],
  disableClear: false,
  onChange: undefined,
  onInputChange: undefined,
  showCheckbox: false,
  formatOption: undefined,
  noOptionsText: 'No options',
  renderOption: undefined,
  sortOptions: false,
  serverSideSearch: false,
  labelTooltip: undefined,
};
